import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Circle,
  CircleCheck,
  Check,
  Square,
  Search,
} from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import SelectAllButton from "@/components/ui/SelectAllButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { normalizeOrgId } from "@/features/esg/utils/org-scope.utils";

const INDENT_SIZE = 20;

const getInitiallyCollapsedNodes = (nodes = [], expandedDepth) => {
  if (!Number.isInteger(expandedDepth)) return new Set();

  const collapsed = new Set();
  const visit = (node, depth) => {
    const children = Array.isArray(node?.children) ? node.children : [];
    if (children.length === 0) return;

    const key = normalizeOrgId(node?.id) || node?.name;
    if (depth >= expandedDepth && key) collapsed.add(key);
    children.forEach((child) => visit(child, depth + 1));
  };

  nodes.forEach((node) => visit(node, 0));
  return collapsed;
};

const toSearchText = (node = {}) =>
  [node.name, node.code, node.country, node.countryLabel, node.locationLabel]
    .filter((v) => typeof v === "string" && v.trim())
    .join(" ")
    .toLowerCase();

const filterTreeByQuery = (nodes = [], query = "") => {
  if (!query) return nodes;

  const isLeaf = (n) => !Array.isArray(n?.children) || n.children.length === 0;

  const markAboveLeaves = (node) => {
    if (isLeaf(node)) return node;
    const newChildren = node.children.map(markAboveLeaves);
    const allLeaves = newChildren.every(isLeaf);
    return allLeaves
      ? { ...node, children: newChildren, _searchMatched: true }
      : { ...node, children: newChildren };
  };

  const filterNode = (node) => {
    const children = Array.isArray(node?.children) ? node.children : [];
    if (toSearchText(node).includes(query)) {
      return markAboveLeaves(node);
    }
    const filteredChildren = children.map(filterNode).filter(Boolean);
    if (filteredChildren.length === 0) return null;
    const allLeaves = filteredChildren.every(isLeaf);
    return allLeaves
      ? { ...node, children: filteredChildren, _searchMatched: true }
      : { ...node, children: filteredChildren };
  };

  return nodes.map(filterNode).filter(Boolean);
};

const getAllChildIds = (node) => {
  let ids = [];

  if (Array.isArray(node?.children)) {
    node.children.forEach((child) => {
      const childId = normalizeOrgId(child?.id || child?._id || child?.key);
      if (childId) {
        ids.push(childId);
      }
      ids = [...ids, ...getAllChildIds(child)];
    });
  }

  return Array.from(new Set(ids));
};

const findNodeName = (nodes = [], targetId) => {
  for (const node of nodes) {
    if (normalizeOrgId(node?.id) === targetId) return node.name;
    if (node?.children) {
      const found = findNodeName(node.children, targetId);
      if (found) return found;
    }
  }
  return null;
};

const findNodePath = (nodes = [], targetId, ancestors = []) => {
  for (const node of nodes) {
    const nodeId = normalizeOrgId(node?.id);
    const path = [...ancestors, node.name];
    if (nodeId === targetId) return path;
    if (node?.children) {
      const found = findNodePath(node.children, targetId, path);
      if (found) return found;
    }
  }
  return null;
};

const OrgTreeNode = ({
  node,
  depth,
  selectedId,
  selectedIds,
  multiSelect,
  collapsedNodes,
  searchExpandedNodes,
  onToggleCollapse,
  onSelect,
  onChange,
  selectedValues,
  showBranchSelectAll,
  branchSelectLevels,
  t,
  codeLabel,
  allowedOrganizationIds = new Set(),
}) => {
  const nodeId = normalizeOrgId(node?.id);
  const hasChildren = node?.children?.length > 0;

  const collapseKey = nodeId || node?.name || "";

  const isSearchMatched = node?._searchMatched === true;

  const isCollapsed = isSearchMatched
    ? !searchExpandedNodes.has(collapseKey)
    : collapsedNodes.has(collapseKey);

  /**
   * Important:
   * Empty allow list means no restriction.
   * Existing modules keep old behavior.
   */
  const hasRestriction = allowedOrganizationIds.size > 0;

  const isAllowed =
    !hasRestriction || allowedOrganizationIds.has(String(nodeId));

  const isSelectable = Boolean(nodeId && !node?.isGroup && isAllowed);

  const isSelected =
    isSelectable &&
    (multiSelect ? selectedIds.has(nodeId) : selectedId === nodeId);

  /**
   * Only allowed IDs should participate
   * in Select All.
   */
  const allRelatedIds =
    showBranchSelectAll && nodeId
      ? [nodeId, ...getAllChildIds(node)].filter((id) => {
          if (!hasRestriction) return true;

          return allowedOrganizationIds.has(String(id));
        })
      : [];

  const nodeUnitLevel = node?.unitLevel || node?.unit_level || "";

  const canShowBranchSelectAll =
    showBranchSelectAll &&
    isSelectable &&
    hasChildren &&
    allRelatedIds.length > 1 &&
    (!Array.isArray(branchSelectLevels) ||
      branchSelectLevels.length === 0 ||
      branchSelectLevels.includes(nodeUnitLevel));

  const areAllSelected =
    allRelatedIds.length > 0 &&
    allRelatedIds.every((relatedId) => selectedIds.has(relatedId));

  const meta = getUnitTypeMeta(node?.unitLevel);
  const Icon = meta.icon;

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-md py-1.5",
          "hover:bg-accent/50",
        )}
        style={{
          paddingLeft: `${6 + depth * INDENT_SIZE}px`,
          paddingRight: 8,
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();

              onToggleCollapse(collapseKey, isSearchMatched);
            }}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
          >
            {isCollapsed ? (
              <ChevronRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-3.5 shrink-0" />
        )}

        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border ${meta.color}`}
        >
          <Icon className="h-3 w-3" />
        </span>

        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span
              title={node?.name || nodeId}
              className="truncate text-xs font-medium text-foreground"
            >
              {node?.name || nodeId}
            </span>

            {hasChildren && (
              <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] font-normal text-muted-foreground">
                {node.children.length}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-nowrap">
            {node?.code && (
              <p className="text-[11px] leading-tight text-muted-foreground shrink-0">
                {codeLabel}: {node.code}
              </p>
            )}
            <OrgStatusTag
              status={node?.status}
              inactiveDate={node?.inactiveDate}
              className="text-[10px]"
            />
          </div>
        </div>

        {canShowBranchSelectAll ? (
          <SelectAllButton
            className="mr-1 shrink-0"
            areAllSelected={areAllSelected}
            value={selectedValues}
            allRelatedIds={allRelatedIds}
            onChange={onChange}
            t={t}
          />
        ) : null}

        {/* 
          Important:
          Only selectable nodes show checkbox.
          Non allowed nodes have no checkbox.
        */}
        {isSelectable && (
          <button
            type="button"
            aria-pressed={isSelected}
            aria-label={
              isSelected ? t("deselect_organization") : t("select_organization")
            }
            onClick={() => onSelect(nodeId)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {multiSelect ? (
              isSelected ? (
                <Check className="h-4 w-4 text-primary" />
              ) : (
                <Square className="h-4 w-4 text-muted-foreground/40" />
              )
            ) : isSelected ? (
              <CircleCheck className="h-4 w-4 text-primary" />
            ) : (
              <Circle className="h-4 w-4 text-muted-foreground/40" />
            )}
          </button>
        )}
      </div>

      {hasChildren &&
        !isCollapsed &&
        node.children.map((child) => (
          <OrgTreeNode
            key={child.id || child.name}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            selectedIds={selectedIds}
            multiSelect={multiSelect}
            collapsedNodes={collapsedNodes}
            searchExpandedNodes={searchExpandedNodes}
            onToggleCollapse={onToggleCollapse}
            onSelect={onSelect}
            onChange={onChange}
            selectedValues={selectedValues}
            showBranchSelectAll={showBranchSelectAll}
            branchSelectLevels={branchSelectLevels}
            t={t}
            codeLabel={codeLabel}
            allowedOrganizationIds={allowedOrganizationIds}
          />
        ))}
    </div>
  );
};

export const OrgTreeSelect = ({
  tree = [],
  value,
  onChange,
  placeholder,
  helperText,
  showAllOption = true,
  multiSelect = false,
  showBranchSelectAll = false,
  branchSelectLevels,
  className,
  disablePortal = false,
  allowOrgs = [],
  disabled = false,
  initialExpandedDepth,
  searchPlaceholder,
}) => {
  const { t } = useTran();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [collapsedNodes, setCollapsedNodes] = useState(null);
  const [searchExpandedNodes, setSearchExpandedNodes] = useState(
    () => new Set(),
  );
  const initiallyCollapsedNodes = useMemo(
    () => getInitiallyCollapsedNodes(tree, initialExpandedDepth),
    [initialExpandedDepth, tree],
  );
  const resolvedCollapsedNodes = collapsedNodes ?? initiallyCollapsedNodes;

  // i18n keys — fallback to common namespace
  const resolvedPlaceholder = placeholder ?? t("all_organizations");
  const resolvedHelperText = helperText ?? t("assigned_org_only");

  const allowedOrganizationIds = useMemo(
    () => new Set((allowOrgs || []).map((id) => String(id))),
    [allowOrgs],
  );
  const query = search.trim().toLowerCase();
  const visibleTree = useMemo(
    () => filterTreeByQuery(tree, query),
    [tree, query],
  );

  const handleSearchChange = (next) => {
    setSearch(next);
    if (!next.trim()) setSearchExpandedNodes(new Set());
  };

  const selectedIds = useMemo(
    () => (multiSelect ? new Set(value || []) : new Set()),
    [multiSelect, value],
  );

  const displayLabel = useMemo(() => {
    if (multiSelect) {
      if (!value || value.length === 0) return resolvedPlaceholder;
      if (value.length === 1) {
        return findNodeName(tree, value[0]) || resolvedPlaceholder;
      }
      // e.g. "3 organizations"
      return t("count_organizations", { count: value.length });
    }

    if (!value || value === "all") return resolvedPlaceholder;
    return findNodeName(tree, value) || resolvedPlaceholder;
  }, [multiSelect, resolvedPlaceholder, t, tree, value]);

  const breadcrumb = useMemo(() => {
    if (multiSelect) return null;
    if (!value || value === "all") return null;
    const path = findNodePath(tree, value);
    if (!path || path.length <= 1) return null;
    return path.join(" / ");
  }, [tree, value, multiSelect]);

  const toggleCollapse = (key, isSearchMatched = false) => {
    const setter = isSearchMatched ? setSearchExpandedNodes : setCollapsedNodes;
    setter((prev) => {
      const next = new Set(
        isSearchMatched ? prev : (prev ?? initiallyCollapsedNodes),
      );
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const select = (id) => {
    if (multiSelect) {
      const next = new Set(selectedIds);
      next.has(id) ? next.delete(id) : next.add(id);
      onChange([...next]);
      return;
    }
    onChange(id);
    setOpen(false);
    setSearch("");
    setSearchExpandedNodes(new Set());
  };

  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(nextOpen) => {
        if (disabled) return;

        if (nextOpen && Number.isInteger(initialExpandedDepth)) {
          setCollapsedNodes(null);
          setSearch("");
          setSearchExpandedNodes(new Set());
        }
        setOpen(nextOpen);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full min-w-0 justify-between bg-transparent px-3 py-1 text-base font-normal dark:bg-background/30 md:text-sm",
            breadcrumb ? "h-auto min-h-9" : "h-9",
            className,
          )}
        >
          <div className="flex min-w-0 flex-1 flex-col items-start text-left">
            <span className="w-full truncate">{displayLabel}</span>
            {breadcrumb && (
              <span className="w-full truncate text-left text-[11px] text-muted-foreground">
                {breadcrumb}
              </span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-fit min-w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-1rem)] p-0"
        align="start"
        disablePortal={disablePortal}
      >
        <div className="flex items-center gap-2 border-b px-2.5 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t("search_organization")}
            className="flex-1 bg-transparent text-xs outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div
          className="max-h-[240px] overflow-y-auto py-1"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {showAllOption && (
            <div
              className="mx-1 flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 hover:bg-accent/50"
              onClick={() => (multiSelect ? onChange([]) : select("all"))}
            >
              <span className="w-3.5 shrink-0" />
              <span className="flex-1 text-xs font-medium">
                {resolvedPlaceholder}
              </span>
              {(multiSelect ? selectedIds.size === 0 : value === "all") ? (
                <CircleCheck className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <Circle className="h-4 w-4 shrink-0 text-muted-foreground/40" />
              )}
            </div>
          )}

          <div className="px-1">
            {visibleTree.map((node) => (
              <OrgTreeNode
                key={node.id || node.name}
                node={node}
                depth={0}
                selectedId={multiSelect ? null : value}
                selectedIds={selectedIds}
                multiSelect={multiSelect}
                collapsedNodes={resolvedCollapsedNodes}
                searchExpandedNodes={searchExpandedNodes}
                onToggleCollapse={toggleCollapse}
                onSelect={select}
                onChange={onChange}
                selectedValues={Array.isArray(value) ? value : []}
                showBranchSelectAll={showBranchSelectAll}
                branchSelectLevels={branchSelectLevels}
                t={t}
                codeLabel={t("code")}
                allowedOrganizationIds={allowedOrganizationIds}
              />
            ))}
          </div>

          {visibleTree.length === 0 && query && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t("no_organizations_found")}
            </p>
          )}
        </div>

        {resolvedHelperText && (
          <div className="border-t px-3 py-2">
            <p className="text-[11px] italic text-muted-foreground">
              {resolvedHelperText}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
